import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { AUDIT_TABLE_DENSITY_KEY } from "@/core/constants/keys";
import { Paths } from "@/core/constants/paths";
import { useTableSettings, useToast } from "@/ds";
import type { FilterValue, HubTableDataTableProps } from "@/ds";
import { useMediaQuery } from "@/hooks/use-media-query";
import { clearAuthToken } from "@/modules/auth";
import { useTableSettingsAppliedToast } from "@/shared/hooks";
import { CONTROL_PANEL_MOBILE_QUERY } from "../control-panel.constants";
import {
  AUDIT_ALL_COLUMNS,
  AUDIT_EMPTY_FILTERED,
  AUDIT_EMPTY_NO_RECORDS,
  AUDIT_TABLE_LABEL,
  AUDIT_TABLE_MIN_WIDTH,
} from "./audit.constants";
import type { AuditColumnKey } from "./audit.constants";
import { buildAuditLinha, fromPanelValue, toPanelValue } from "./audit.utils";
import { useAuditExportacao } from "./hooks/use-audit-exportacao";
import { useAuditFiltrosColunas } from "./hooks/use-audit-filtros-colunas";
import { useAuditLista } from "./hooks/use-audit-lista";
import type { AuditLinha, AuditViewModel } from "./types-audit";

/** Aba Auditoria do Painel (HU 744, 834, 1555, 1556, 1557 e a exportação da 1442): lista, filtros, detalhe e menu ⋮. */
export function useAudit(): AuditViewModel {
  const navigate = useNavigate();
  const isMobile = useMediaQuery(CONTROL_PANEL_MOBILE_QUERY);
  const toast = useToast();
  // O registro aberto não trafega na URL: a listagem segue montada por trás do modal (AC4).
  const [detalhando, setDetalhando] = useState<AuditLinha | null>(null);

  const settings = useTableSettings<AuditColumnKey>({
    densityKey: AUDIT_TABLE_DENSITY_KEY,
    allColumns: AUDIT_ALL_COLUMNS,
  });
  const notifySettingsApplied = useTableSettingsAppliedToast(toast);
  // Lido na montagem: a aba aberta na virada do ano segue no ano em que abriu até recarregar.
  const [anoCorrente] = useState(() => new Date().getFullYear());
  const filterColumns = useAuditFiltrosColunas(anoCorrente);
  const lista = useAuditLista(anoCorrente);
  const aplicarFiltros = (value: FilterValue) => lista.applyFilters(fromPanelValue(value));
  const exportView = useAuditExportacao({
    colunas: settings.visibleColumns,
    filters: lista.filters,
    sort: lista.appliedSort,
    filterColumns,
    isEmpty: lista.status === "success" && lista.total === 0,
    toast,
  });

  const linhas = useMemo(() => lista.items.map(buildAuditLinha), [lista.items]);
  const filterValue = useMemo(() => toPanelValue(lista.filters), [lista.filters]);

  // O corpo rola dentro do cartão; abaixo da largura mínima a tabela rola na horizontal.
  const dataTableProps: HubTableDataTableProps = {
    scrollable: true,
    scrollHeight: "flex",
    tableStyle: { minWidth: AUDIT_TABLE_MIN_WIDTH },
    "aria-label": AUDIT_TABLE_LABEL,
  };

  return {
    isMobile,
    filterColumns,
    filterValue,
    aplicarFiltros,
    settings,
    exportView,
    notifySettingsApplied,
    items: linhas,
    total: lista.total,
    isFirstLoad: lista.status === "loading" && linhas.length === 0,
    status: lista.status,
    errorMessage: lista.errorMessage,
    reload: lista.reload,
    table: lista.tabela,
    dataTableProps,
    hasFilters: lista.hasFilters,
    vazio: lista.hasFilters ? AUDIT_EMPTY_FILTERED : AUDIT_EMPTY_NO_RECORDS,
    limparFiltros: lista.clearFilters,
    detalhando,
    detalhar: (linha) => setDetalhando(linha),
    fecharDetalhe: () => setDetalhando(null),
    reautenticar: () => {
      clearAuthToken();
      navigate(Paths.LOGIN, { replace: true });
    },
  };
}
